import { useEffect, useState } from "react"
import classNames from "classnames"
import styled from "styled-components"

import { useLazyGetWHODevicesWidgetQuery } from "store/api/warehouseManagerApi"

import { WidgetLayout } from "layouts/WidgetLayout"
import { BarChart } from "components/common/BarChart"
import { Spinner } from "components/common/Spinner"

import styles from "./index.module.scss"

const StyledBarChart = styled(BarChart)`
  padding: 0 60px;

  @media only screen and (max-width: 1240px) {
    padding: 0;
  }
`

const initialState = {
  packedDevices: { v1: 0, v2: 0 },
  availableForPacking: { v1: 0, v2: 0 },
  allDevices: { v1: 0, v2: 0 },
}

const colors = {
  availableV1: "#2563EB",
  availableV2: "#3B82F6",
  packedV1: "#93C5FD",
  packedV2: "#DBEAFE",
}

interface WidgetWarehouseProps {
  updateIndex: number
}

export const WidgetWarehouse = ({ updateIndex }: WidgetWarehouseProps) => {
  const [widgetData, setWidgetData] = useState(initialState)
  const [triggerGetWHODevices, { isFetching, data }] = useLazyGetWHODevicesWidgetQuery()

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await triggerGetWHODevices()
        if (res?.data?.data) {
          setWidgetData({
            packedDevices: { v1: res.data.data.packed_devices_v1, v2: res.data.data.packed_devices_v2 },
            availableForPacking: {
              v1: res.data.data.available_for_packing_v1,
              v2: res.data.data.available_for_packing_v2,
            },
            allDevices: { v1: res.data.data.all_devices_v1, v2: res.data.data.all_devices_v2 },
          })
        }
      } catch (error) {
        console.error(error)
      }
    }

    fetchData()
  }, [updateIndex])

  const loaded = !isFetching && !!data?.data
  const chartTotal =
    widgetData.availableForPacking.v1 +
    widgetData.availableForPacking.v2 +
    widgetData.packedDevices.v1 +
    widgetData.packedDevices.v2
  const hasNoData = loaded && chartTotal === 0

  return (
    <WidgetLayout title="Warehouse" address={data?.data?.address} className={styles.widgetWrapper}>
      <div
        className={classNames(styles.widgetContent, {
          [styles.disabled]: isFetching,
        })}
      >
        <StyledBarChart
          showEmptyState={hasNoData}
          items={
            loaded
              ? [
                  { value: widgetData.availableForPacking.v1, color: colors.availableV1 },
                  { value: widgetData.availableForPacking.v2, color: colors.availableV2 },
                  { value: widgetData.packedDevices.v1, color: colors.packedV1 },
                  { value: widgetData.packedDevices.v2, color: colors.packedV2 },
                ]
              : [{ value: 1, color: "#E2E8F0" }]
          }
        />
        {loaded && !hasNoData && (
          <>
            <div className={styles.widgetMainValue}>
              <span className={styles.text}>THERMOSTATS</span>
              <span className={styles.mainValue}>
                <span>{widgetData.allDevices.v1}</span>
                <span className={styles.versionNote}>(V1)</span>
                <span>/</span>
                <span>{widgetData.allDevices.v2}</span>
                <span className={styles.versionNote}>(V2)</span>
              </span>
            </div>
            <footer className={styles.widgetFooter}>
              <div className={styles.widgetFooterItem}>
                <span className={classNames(styles.widgetNote, styles.dot, styles.availableV1)}>Available V1</span>
                <div className={styles.widgetValueWrapper}>{widgetData.availableForPacking.v1}</div>
              </div>
              <div className={styles.widgetFooterItem}>
                <span className={classNames(styles.widgetNote, styles.dot, styles.availableV2)}>Available V2</span>
                <div className={styles.widgetValueWrapper}>{widgetData.availableForPacking.v2}</div>
              </div>
              <div className={styles.widgetFooterItem}>
                <span className={classNames(styles.widgetNote, styles.dot, styles.packedV1)}>Packed V1</span>
                <div className={styles.widgetValueWrapper}>{widgetData.packedDevices.v1}</div>
              </div>
              <div className={styles.widgetFooterItem}>
                <span className={classNames(styles.widgetNote, styles.dot, styles.packedV2)}>Packed V2</span>
                <div className={styles.widgetValueWrapper}>{widgetData.packedDevices.v2}</div>
              </div>
            </footer>
          </>
        )}
        {isFetching && (
          <div className={styles.widgetLoader}>
            <Spinner />
          </div>
        )}
      </div>
    </WidgetLayout>
  )
}
